import { CanAccess } from '@modules/common/components/CanAccess';
import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useDeleteUnifiedField } from '@modules/ManageUnifiedFields/hooks';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';

interface DeleteUnifiedFieldConfirmDialogContentProps {
  name?: string;
  onClose: () => void;
  closeDrawer: () => void;
  metadataKey: string;
  onSubmit?: () => void;
}

export const DeleteUnifiedFieldConfirmDialogContent = ({
  name,
  onClose,
  closeDrawer,
  metadataKey,
  onSubmit,
}: DeleteUnifiedFieldConfirmDialogContentProps) => {
  const { deleteMetadataKey, loading } = useDeleteUnifiedField();
  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const onDelete = async () => {
    try {
      await deleteMetadataKey(metadataKey);
      onSubmit?.();
      onClose();
      closeDrawer();
      showSuccessNotification({
        contentComponent: `Successfully deleted Unified Field - ${name}`,
        props: {},
      });
    } catch {
      showErrorNotification({
        contentComponent: `Failed to delete Unified Field - ${name}`,
        props: {},
      });
    }
  };

  return (
    <>
      <DialogTitle>
        {name ? (
          <>Delete {name} as a Unified Field? </>
        ) : (
          <>Delete this Unified Field?</>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" pb={2}>
          Fields that were hidden while creating{' '}
          {name ? `${name}` : 'this Unified Field'} will stay hidden. You can
          toggle their visibility by managing metadata on the Integrations page.
        </Typography>
        <Alert severity="info" variant="outlined">
          Saved Items using this Unified Field will be affected
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          variant="outlined"
          onClick={() => onClose()}
          endIcon={<EscHotKeyIcon />}
        >
          Cancel
        </Button>
        <CanAccess
          resource={PermissionResource.Metadata}
          operation={PermissionOperation.Write}
        >
          <LoadingButton
            variant="contained"
            color="error"
            onClick={onDelete}
            loading={loading}
          >
            Delete
          </LoadingButton>
        </CanAccess>
      </DialogActions>
    </>
  );
};
